/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { omit, pick } from 'ramda';
import * as React from 'react';

import colors from '../../../theme/colors';
import { borderRadius, spacingKeys, spacings } from '../../../theme/variables';
import Button from '../../Button/Button';
import { ButtonProps } from '../../Button/Button.types';
import Container from '../Container';
import {
  AddIcon,
  ArrowLeft2Icon,
  ChevronLeft,
  ChevronRight,
  ClearIcon,
  CloseIcon,
  ContactCalendarIcon,
  DeleteIcon,
  DownloadIcon,
  EditIcon,
  EditOutlineIcon,
  FullscreenIcon,
  MoreIcon,
  OpenInNewArrowlessIcon,
  OpenInNewIcon,
  PictureInPictureIcon,
  QuestionMarkHollowIcon,
  QuestionMarkIcon,
  SwapIcon,
  UserMinusIcon,
  ZoomInIcon,
  ZoomOutIcon
} from '../icons';

export enum ButtonIcons {
  Add = 'add',
  Edit = 'edit',
  Delete = 'delete',
  More = 'more',
  FullScreen = 'full_screen',
  Close = 'close',
  PictureInPicture = 'pip',
  Clear = 'clear',
  OpenInNew = 'open_in_new',
  OpenInNewArrowless = 'open_in_new_arrowless',
  ChevronLeft = 'chevron_left',
  ChevronRight = 'chevron_right',
  QuestionMark = 'question_mark',
  QuestionMarkHollow = 'question_mark_hollow',
  ContactCalendar = 'contact_calendar',
  Download = 'download',
  ArrowLeft2 = 'arrow_left',
  UserMinus = 'user_minus',
  Swap = 'swap',
  ZoomIn = 'zoom_in',
  ZoomOut = 'zoom_out',
  EditOutline = 'edit_outline'
}

interface IIconButton extends Omit<ButtonProps, 'children'> {
  icon: ButtonIcons;
  focus?: boolean;
  color?: string;
  badge?: boolean;
  badgeColor?: string;
}

const generateIcon = ({ icon, color }: { icon: ButtonIcons; color: string }) => {
  switch (icon) {
    case ButtonIcons.Add:
      return <AddIcon color={color} />;
    case ButtonIcons.Edit:
      return <EditIcon color={color} />;
    case ButtonIcons.EditOutline:
      return <EditOutlineIcon color={color} />;
    case ButtonIcons.Delete:
      return <DeleteIcon color={color} />;
    case ButtonIcons.More:
      return <MoreIcon color={color} />;
    case ButtonIcons.FullScreen:
      return <FullscreenIcon color={color} />;
    case ButtonIcons.Close:
      return <CloseIcon color={color} />;
    case ButtonIcons.PictureInPicture:
      return <PictureInPictureIcon color={color} />;
    case ButtonIcons.Clear:
      return <ClearIcon color={color} />;
    case ButtonIcons.OpenInNew:
      return <OpenInNewIcon color={color} />;
    case ButtonIcons.OpenInNewArrowless:
      return <OpenInNewArrowlessIcon color={color} />;
    case ButtonIcons.ChevronLeft:
      return <ChevronLeft color={color} />;
    case ButtonIcons.ChevronRight:
      return <ChevronRight color={color} />;
    case ButtonIcons.QuestionMark:
      return <QuestionMarkIcon color={color} />;
    case ButtonIcons.QuestionMarkHollow:
      return <QuestionMarkHollowIcon color={color} />;
    case ButtonIcons.ContactCalendar:
      return <ContactCalendarIcon color={color} />;
    case ButtonIcons.Download:
      return <DownloadIcon color={color} />;
    case ButtonIcons.ArrowLeft2:
      return <ArrowLeft2Icon color={color} />;
    case ButtonIcons.UserMinus:
      return <UserMinusIcon color={color} width={20} height={20} />;
    case ButtonIcons.Swap:
      return <SwapIcon color={color} />;
    case ButtonIcons.ZoomIn:
      return <ZoomInIcon color={color} width={20} height={20} />;
    case ButtonIcons.ZoomOut:
      return <ZoomOutIcon color={color} width={20} height={20} />;

    default:
      return '';
  }
};

const IconButton = ({
  onClick,
  focus,
  customCss,
  icon,
  color = colors.blue400,
  disabled = false,
  badge,
  badgeColor = colors.cherise50,
  ...props
}: IIconButton) => {
  const spacingProps = pick(spacingKeys, props);
  const restOfProps = omit(spacingKeys, props);
  return (
    <Button
      borderless
      p={spacings.px4}
      disabled={disabled}
      customCss={css`
        background-color: ${focus ? colors.neutral100 : 'transparent'};
        border-radius: ${borderRadius}px;
        position: relative;
        &:hover {
          background-color: ${colors.neutral100};
        }
        line-height: 1;
        ${customCss}
      `}
      onClick={onClick}
      {...spacingProps}
      {...restOfProps}
    >
      {generateIcon({ icon, color: disabled ? colors.neutral300 : color })}
      {badge && (
        <Container
          customCss={css`
            position: absolute;

            top: 8px;
            right: 8px;
            width: 8px;
            height: 8px;

            border-radius: 4px;

            background-color: ${badgeColor};
          `}
        />
      )}
    </Button>
  );
};

export default IconButton;
